/**
 * Go Gets: the workbook's Go Gets tabs -- the plan on SKUs that have never
 * shipped (the reps' and prospects' placeholders, and the MOSHERA / MOSHERNA
 * adjustment lines), by SKU and month, for the whole fiscal year: a plan runs
 * past the last shipped month. One table for both businesses, a column saying
 * which.
 */

import { Card, Empty } from '../shared/Card'
import type { Keyed } from '../shared/rows'
import { text } from '../shared/rows'
import { amount } from './format'
import { MONTHS } from './tree'

function num(value: Keyed[string] | undefined): number {
  return typeof value === 'number' ? value : Number(value ?? 0)
}

const KEYS = MONTHS.map((_name, index) => `plan_${String(index + 1).padStart(2, '0')}`)

export function GoGets({
  rows,
  planLabel,
  fyLabel,
  money,
}: {
  rows: readonly Keyed[]
  planLabel: string
  fyLabel: string
  money: boolean
}) {
  const totals = KEYS.map((key) => rows.reduce((total, row) => total + num(row[key]), 0))
  const grand = rows.reduce((total, row) => total + num(row['total']), 0)
  return (
    <Card
      title={`Go Gets: ${planLabel.toLowerCase()} by month, ${fyLabel}`}
      subtitle="Plan on SKUs that have never shipped: placeholders for reps and prospects, and the MOSHERA / MOSHERNA adjustment lines. The whole year, including months not yet shipped."
    >
      {rows.length === 0 ? (
        <Empty label="No Go Gets in these filters" />
      ) : (
        <div className="srp-scroll">
          <table className="srp-table rcp-table rcp-months rcp-gogets">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Business</th>
                {MONTHS.map((name) => (
                  <th key={name} className="cell-number">
                    {name}
                  </th>
                ))}
                <th className="cell-number">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${text(row['business']) ?? ''}/${text(row['sku']) ?? ''}`}>
                  <td className="srp-wrap">{text(row['sku'])}</td>
                  <td>{text(row['business'])}</td>
                  {KEYS.map((key) => (
                    <td key={key} className="cell-number">
                      {amount(num(row[key]), money)}
                    </td>
                  ))}
                  <td className="cell-number srp-strong">{amount(num(row['total']), money)}</td>
                </tr>
              ))}
              <tr className="srp-total">
                <td colSpan={2}>Grand total</td>
                {totals.map((value, index) => (
                  <td key={KEYS[index]} className="cell-number">
                    {amount(value, money)}
                  </td>
                ))}
                <td className="cell-number">{amount(grand, money)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
